import { formatGbp } from "../lib/format.ts";

/**
 * A price, with the menu price crossed out when an offer is running.
 *
 * One component for all four places a price appears - the homepage menu, the
 * booking picker, the summary before payment, and the admin treatment list - so
 * a discounted price can never look like a full one in one of them.
 *
 * `<s>` rather than styling alone: the old price being superseded is a fact about
 * the content, not a decoration, and a screen reader should say so. The visually
 * hidden words carry what the strikethrough means for anyone who cannot see it,
 * because "£95 £76" read aloud is just two numbers.
 */
interface PriceProps {
  /** What the client pays. */
  pricePence: number;
  /** The menu price. Crossed out only when it differs. */
  originalPence: number;
  /** Extra classes for the price itself, so callers keep their own type scale. */
  className?: string;
}

export function Price({ pricePence, originalPence, className }: PriceProps) {
  const discounted = originalPence > pricePence;

  if (!discounted) {
    return <span className={className}>{formatGbp(pricePence)}</span>;
  }

  return (
    <span className={`price price--offer ${className ?? ""}`}>
      <span className="visually-hidden">Was </span>
      <s className="price__was">{formatGbp(originalPence)}</s>
      <span className="visually-hidden">, now </span>
      <span className="price__now">{formatGbp(pricePence)}</span>
    </span>
  );
}

/** The offer's name, as a small badge to sit beside a price. */
export function OfferBadge({
  label,
  descriptor,
}: {
  label: string;
  descriptor?: string | null;
}) {
  return (
    <span className="offer-badge">
      {label}
      {descriptor && <span className="offer-badge__amount">{descriptor}</span>}
    </span>
  );
}
